import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const StatCard = ({ label, value, icon, color, sub }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow min-h-[120px]">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0 mr-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
    </div>
  </div>
);

export default function SuperadminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-center text-gray-500 py-10">Failed to load dashboard</div>;

  const { tenants, subscriptions, revenue, plan_distribution, platform_users, unread_notifications, recent_activity, recent_tenants } = data;

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold">Platform Overview</h2>
        <p className="text-blue-200 mt-1 text-sm">Monitor all tenants, subscriptions, and platform health at a glance.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Total Tenants" value={tenants.total} icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" color="bg-blue-500" />
        <StatCard label="Active" value={tenants.active} icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" color="bg-green-500" />
        <StatCard label="Suspended" value={tenants.suspended} icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" color="bg-amber-500" />
        <StatCard label="Expiring Soon" value={subscriptions.expiring_soon} sub="Next 30 days" icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" color="bg-orange-500" />
        <StatCard label="Revenue" value={`₹${(revenue.active / 1000).toFixed(0)}K`} sub="Active subs" icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" color="bg-emerald-600" />
        <StatCard label="Platform Users" value={platform_users} icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" color="bg-purple-500" />
      </div>

      {/* Alerts */}
      {(subscriptions.expired > 0 || subscriptions.expiring_soon > 0 || unread_notifications > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {subscriptions.expired > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3 cursor-pointer hover:bg-red-100 transition-colors" onClick={() => navigate('/subscriptions')}>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
              <div><div className="text-sm font-semibold text-red-800">{subscriptions.expired} Expired Subscription(s)</div><div className="text-xs text-red-600">Action required</div></div>
            </div>
          )}
          {subscriptions.expiring_soon > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center space-x-3 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => navigate('/subscriptions')}>
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
              <div><div className="text-sm font-semibold text-amber-800">{subscriptions.expiring_soon} Expiring Soon</div><div className="text-xs text-amber-600">Within 30 days</div></div>
            </div>
          )}
          {unread_notifications > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg></div>
              <div><div className="text-sm font-semibold text-blue-800">{unread_notifications} Unread Notification(s)</div></div>
            </div>
          )}
        </div>
      )}

      {/* Plan Distribution + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plan Distribution */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Plan Distribution</h3>
          <div className="space-y-3">
            {['basic', 'standard', 'premium'].map(plan => {
              const count = plan_distribution[plan] || 0;
              const pct = tenants.total > 0 ? Math.round((count / tenants.total) * 100) : 0;
              const colors = { basic: 'bg-gray-400', standard: 'bg-blue-500', premium: 'bg-indigo-600' };
              return (
                <div key={plan}>
                  <div className="flex justify-between text-sm"><span className="capitalize text-gray-700">{plan}</span><span className="font-semibold text-gray-900">{count}</span></div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mt-1"><div className={`${colors[plan]} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { label: 'Register New Tenant', to: '/tenants/new', color: 'text-blue-600 bg-blue-50 hover:bg-blue-100' },
              { label: 'Manage Subscriptions', to: '/subscriptions', color: 'text-green-600 bg-green-50 hover:bg-green-100' },
              { label: 'Generate Reports', to: '/reports', color: 'text-purple-600 bg-purple-50 hover:bg-purple-100' },
              { label: 'View Audit Logs', to: '/audit', color: 'text-gray-600 bg-gray-50 hover:bg-gray-100' },
            ].map(a => (
              <button key={a.to} onClick={() => navigate(a.to)} className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${a.color}`}>{a.label}</button>
            ))}
          </div>
        </div>

        {/* Recent Tenants */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Tenants</h3>
          <div className="space-y-3">
            {recent_tenants?.map(t => (
              <div key={t.code} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors" onClick={() => navigate(`/tenants/${t.code}`)}>
                <div>
                  <div className="text-sm font-medium text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.code} &middot; {t.subscription_plan}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.status === 'active' ? 'bg-green-100 text-green-700' : t.status === 'suspended' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Recent Activity</h3>
          <button onClick={() => navigate('/audit')} className="text-xs text-blue-600 hover:text-blue-700 font-medium">View All</button>
        </div>
        {recent_activity?.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No recent activity</p>
        ) : (
          <div className="space-y-2">
            {recent_activity?.slice(0, 8).map(a => (
              <div key={a.id} className="flex items-center space-x-3 py-2 border-b border-gray-50 last:border-0">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.action?.includes('CREATE') ? 'bg-green-500' : a.action?.includes('DELETE') || a.action?.includes('DISABLE') ? 'bg-red-500' : a.action?.includes('TOGGLE') ? 'bg-amber-500' : 'bg-blue-500'}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-900">{a.action?.replace(/_/g, ' ')}</span>
                  {a.tenant_code && <span className="text-sm text-gray-400 ml-1">({a.tenant_code})</span>}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{a.performed_by_name} &middot; {new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
